import React from 'react';
import './CompanyReportDisplay.css';

const CompanyReportDisplay = ({ results, query, searchType }) => {
  if (!results || results.length === 0) {
    return (
      <div className="company-report-display no-results">
        <div className="no-results-message">
          <span className="no-results-icon">🔍</span>
          <p>No company reports found for your search.</p>
          <p className="no-results-suggestion">Try adjusting your search criteria or using different keywords.</p>
        </div>
      </div>
    );
  }

  const renderFinancials = (financials) => {
    if (!financials || Object.keys(financials).length === 0) return null;
    
    const metrics = {
      'Total Revenue': financials.total_revenue,
      'Net Profit': financials.net_profit,
      'EBITDA': financials.ebitda,
      'EPS': financials.earnings_per_share_eps,
      'Debt/Equity': financials.debt_to_equity_ratio,
      'ROE': financials.return_on_equity_roe,
    };

    return Object.entries(metrics).map(([key, value]) => {
      if (!value) return null;
      return (
        <div key={key} className="metric-item">
          <span className="metric-label">{key}</span>
          <span className="metric-value">{value}</span>
        </div>
      );
    });
  };

  const renderOperationalHighlights = (highlights) => {
    if (!highlights || !highlights.key_achievements) return null;
    
    const achievements = highlights.key_achievements.split('\n- ').filter(item => item);
    return achievements.map((item, index) => (
      <li key={index}>{item.replace(/^- /, '')}</li>
    ));
  };

  const renderProsAndCons = (prosAndCons) => {
    if (!prosAndCons) return null;
    
    return (
      <>
        <div className="card">
          <h3>Pros</h3>
          <ul className="pros-cons-list">
            {prosAndCons.pros?.map((pro, index) => (
              <li key={index} className="pro-item">{pro}</li>
            ))}
          </ul>
        </div>
        <div className="card">
          <h3>Cons / Risks</h3>
          <ul className="pros-cons-list">
            {prosAndCons.cons?.map((con, index) => (
              <li key={index} className="con-item">{con}</li>
            ))}
          </ul>
        </div>
      </>
    );
  };

  return (
    <div className="company-report-display">
      <div className="results-header">
        <div className="search-summary">
          <h3 className="results-title">
            📋 Company Report Search Results
          </h3>
          {query && (
            <p className="search-query">
              <strong>Search:</strong> "{query}"
            </p>
          )}
          <p className="results-count">
            Found {results.length} report{results.length !== 1 ? 's' : ''}
          </p>
          <p className="search-type">
            <small>Search method: {searchType}</small>
          </p>
        </div>
      </div>

      <div className="results-list">
        {results.map((report, index) => (
          <div key={index} className="report-card">
            <div className="report-header">
              <h4>{report.company_name || 'Company'} - {report.fiscal_year || 'Report'}</h4>
              {report.source && (
                <div className="report-source">
                  <small>Source: {report.source}</small>
                </div>
              )}
            </div>

            <div className="report-content">
              {/* Overall Summary */}
              {report.overall_summary && (
                <div className="summary-section card">
                  <h3>📝 Executive Summary</h3>
                  <p>{report.overall_summary}</p>
                </div>
              )}

              {/* Content Summary */}
              {report.content && (
                <div className="content-section card">
                  <h3>📄 Content Summary</h3>
                  <p>{report.content}</p>
                </div>
              )}

              <div className="report-grid">
                {/* Financial Metrics */}
                {report.financials && (
                  <div className="card">
                    <h3>💰 Key Financial Metrics</h3>
                    <div className="metrics-grid">
                      {renderFinancials(report.financials)}
                    </div>
                  </div>
                )}

                {/* Operational Highlights */}
                {report.operational_highlights && (
                  <div className="card">
                    <h3>🚀 Operational Highlights</h3>
                    <ul className="highlights-list">
                      {renderOperationalHighlights(report.operational_highlights)}
                    </ul>
                  </div>
                )}

                {/* Pros and Cons */}
                {renderProsAndCons(report.pros_and_cons)}

                {/* Management Discussion */}
                {report.management_discussion && (
                  <div className="card">
                    <h3>💼 Management Discussion</h3>
                    <p>{report.management_discussion.strategy_and_outlook || 'No management discussion available.'}</p>
                  </div>
                )}

                {/* Equity Information */}
                {report.equity_info && (
                  <div className="card">
                    <h3>📊 Equity Information</h3>
                    <div className="equity-details">
                      {report.equity_info.face_value && (
                        <div className="equity-item">
                          <span className="equity-label">Face Value:</span>
                          <span className="equity-value">{report.equity_info.face_value}</span>
                        </div>
                      )}
                      {report.equity_info.market_cap && (
                        <div className="equity-item">
                          <span className="equity-label">Market Cap:</span>
                          <span className="equity-value">{report.equity_info.market_cap}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Report Footer */}
            <div className="report-footer">
              <div className="report-meta">
                {report.analysis_date && (
                  <span className="analysis-date">
                    📅 Analyzed: {new Date(report.analysis_date).toLocaleDateString()}
                  </span>
                )}
                {report.currency && (
                  <span className="currency">
                    💱 Currency: {report.currency}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyReportDisplay;
